import { memo, ReactNode, useCallback } from "react";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme, Theme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { fMessageTime } from "src/utils/formatChatTime";
import { fCurrency } from "src/utils/formatNumber";
import { fDateTime } from "src/utils/formatTime";
import Iconify from "src/components/iconify";
import { ChatMessage, ChatSender } from "src/@types/chat-message";
import { bubbleColor, bubbleSx, senderColor } from "./chatStyles";

// ----------------------------------------------------------------------

type Props = {
  message: ChatMessage;
  isFirstOfBlock: boolean;
  onOpenMedia: (url: string) => void;
};

/** Largura reservada na última linha do texto para a hora não sobrepor. */
const TIME_SPACER_WIDTH = 46;

// ----------------------------------------------------------------------
// Formatação inline do WhatsApp
// ----------------------------------------------------------------------

const INLINE_RE = /(https?:\/\/[^\s<]+)|\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~|`([^`\n]+)`/g;

const OPENERS = /[\s(["'—-]/;
const CLOSERS = /[\s.,!?)\]}"':;—-]/;

/**
 * Aplica *negrito*, _itálico_, ~riscado~, `mono` e transforma URLs em links.
 * Não usa react-markdown de propósito — ele interpretaria muito mais do que o
 * WhatsApp e estragaria mensagens comuns.
 *
 * Os delimitadores só valem em fronteira de palavra, senão `2*3*4` viraria
 * negrito. Sem `matchAll`/`replaceAll`: o tsconfig alveja es5.
 */
function formatInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  INLINE_RE.lastIndex = 0;

  // eslint-disable-next-line no-cond-assign
  while ((match = INLINE_RE.exec(text)) !== null) {
    const [full, url, bold, italic, strike, mono] = match;

    if (!url) {
      const before = match.index > 0 ? text[match.index - 1] : "";
      const after = text[match.index + full.length] || "";
      const validBoundary =
        (before === "" || OPENERS.test(before)) &&
        (after === "" || CLOSERS.test(after));

      if (!validBoundary) continue;
    }

    if (match.index > cursor) nodes.push(text.slice(cursor, match.index));

    const key = `${match.index}-${full.length}`;

    if (url) {
      nodes.push(
        <Box
          key={key}
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "info.main", textDecoration: "underline" }}
        >
          {url}
        </Box>
      );
    } else if (bold) {
      nodes.push(<strong key={key}>{bold}</strong>);
    } else if (italic) {
      nodes.push(<em key={key}>{italic}</em>);
    } else if (strike) {
      nodes.push(<s key={key}>{strike}</s>);
    } else if (mono) {
      nodes.push(
        <Box
          key={key}
          component="code"
          sx={{ fontFamily: "monospace", fontSize: "0.92em" }}
        >
          {mono}
        </Box>
      );
    }

    cursor = match.index + full.length;
  }

  if (cursor < text.length) nodes.push(text.slice(cursor));

  return nodes;
}

// ----------------------------------------------------------------------
// Peças da bolha
// ----------------------------------------------------------------------

/** Rabicho no topo da bolha. SVG porque o formato é côncavo, não triangular. */
function BubbleTail({ isClient, color }: { isClient: boolean; color: string }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 8 13"
      width={8}
      height={13}
      aria-hidden
      focusable="false"
      sx={{
        position: "absolute",
        top: 0,
        display: "block",
        ...(isClient ? { left: -7 } : { right: -7 }),
        filter: "drop-shadow(0 1px 0.5px rgba(11,20,26,0.13))",
      }}
    >
      <path
        d={
          isClient
            ? "M8 0H1.54C.24 0 -0.29 1.02 0.52 1.96L8 10.7V0z"
            : "M0 0h6.46c1.3 0 1.83 1.02 1.02 1.96L0 10.7V0z"
        }
        fill={color}
      />
    </Box>
  );
}

/**
 * Espaçador invisível no fim do texto: reserva lugar na última linha para a
 * hora, que é absoluta. Altura zero para não empurrar a bolha.
 */
function TimeSpacer() {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: "inline-block",
        width: `${TIME_SPACER_WIDTH}px`,
        height: 0,
        verticalAlign: "bottom",
      }}
    />
  );
}

function BubbleTime({
  timestamp,
  overlay,
}: {
  timestamp: string | null;
  overlay?: boolean;
}) {
  if (!timestamp) return null;

  return (
    <Typography
      component="span"
      sx={{
        position: "absolute",
        right: overlay ? 10 : 8,
        bottom: overlay ? 8 : 4,
        fontSize: 11,
        lineHeight: 1,
        pointerEvents: "none",
        userSelect: "none",
        ...(overlay
          ? {
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              color: "common.white",
              bgcolor: "rgba(0,0,0,0.45)",
            }
          : { color: "text.disabled" }),
      }}
    >
      {fMessageTime(timestamp)}
    </Typography>
  );
}

function SenderTag({ sender, theme }: { sender: ChatSender; theme: Theme }) {
  if (!sender.label) return null;

  return (
    <Typography
      component="div"
      variant="caption"
      sx={{
        display: "block",
        mb: 0.25,
        fontWeight: 700,
        fontSize: 12.5,
        lineHeight: 1.4,
        color: senderColor(theme, sender),
      }}
    >
      {sender.label}
    </Typography>
  );
}

/** Chip central para campanha, follow-up e avisos internos. */
function SystemNotice({
  title,
  text,
  timestamp,
}: {
  title: string;
  text: string | null;
  timestamp: string | null;
}) {
  return (
    <Stack alignItems="center" sx={{ my: 1.5 }}>
      <Stack
        spacing={0.5}
        sx={{
          maxWidth: 460,
          px: 1.75,
          py: 1,
          borderRadius: 1,
          textAlign: "center",
          bgcolor: "rgba(255,241,213,0.92)",
          color: "grey.800",
          boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)",
        }}
      >
        <Typography variant="caption" fontWeight={700}>
          {title}
        </Typography>

        {text && (
          <Typography variant="caption" sx={{ whiteSpace: "pre-wrap" }}>
            {formatInline(text)}
          </Typography>
        )}

        {timestamp && (
          <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.7 }}>
            {fMessageTime(timestamp)}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

function ExpiredMedia({ expiresAt }: { expiresAt: number | null }) {
  return (
    <Stack spacing={0.25} sx={{ py: 0.5 }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Iconify icon="eva:clock-outline" width={16} />
        <Typography variant="subtitle2" fontWeight={700}>
          Mídia expirada
        </Typography>
      </Stack>
      {expiresAt && (
        <Typography variant="caption" color="text.secondary">
          Expirou em {fDateTime(expiresAt)}
        </Typography>
      )}
    </Stack>
  );
}

/** Card interno usado por conteúdos que não são texto solto. */
const innerCardSx = {
  p: 1,
  borderRadius: 1,
  bgcolor: "rgba(0,0,0,0.04)",
} as const;

// ----------------------------------------------------------------------

function getFileExtension(url: string) {
  if (!url) return null;

  const clean = url.split("?")[0].split("#")[0];
  const filename = clean.split("/").pop() || "";
  const extension = filename.indexOf(".") >= 0 ? filename.split(".").pop() : "";

  return extension ? extension.toLowerCase() : null;
}

function getDocumentIcon(extension: string | null) {
  if (!extension) return "eva:file-fill";
  if (extension === "pdf") return "ant-design:file-pdf-filled";
  if (["doc", "docx"].indexOf(extension) >= 0) return "vscode-icons:file-type-word";
  if (["xls", "xlsx", "csv"].indexOf(extension) >= 0)
    return "vscode-icons:file-type-excel";
  if (["ppt", "pptx"].indexOf(extension) >= 0)
    return "vscode-icons:file-type-powerpoint";
  if (["zip", "rar", "7z", "tar", "gz"].indexOf(extension) >= 0) return "mdi:zip-box";
  if (["txt", "rtf"].indexOf(extension) >= 0) return "vscode-icons:file-type-text";
  return "eva:file-fill";
}

// ----------------------------------------------------------------------

function ChatMessageItem({ message, isFirstOfBlock, onOpenMedia }: Props) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const handleCopy = useCallback(
    async (value: string, label: string) => {
      if (!value) return;

      try {
        await navigator.clipboard.writeText(value);
        enqueueSnackbar(`${label} copiado.`);
      } catch {
        enqueueSnackbar(`Erro ao copiar ${label.toLowerCase()}.`, {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar]
  );

  if (message.type === "system") {
    return (
      <SystemNotice
        title={message.title}
        text={message.text}
        timestamp={message.timestamp}
      />
    );
  }

  const isClient = message.role === "client";
  const timestamp = message.timestamp;

  /** Texto simples com a hora encaixada na última linha. */
  const textBubble = (text: string) => (
    <Typography component="div" variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
      {formatInline(text)}
      <TimeSpacer />
    </Typography>
  );

  /** Mídia clicável: a hora vira pílula sobre a imagem, como no WhatsApp. */
  const mediaFrame = (url: string, children: ReactNode, clickable = true) => (
    <Box
      onClick={clickable ? () => onOpenMedia(url) : undefined}
      sx={{
        position: "relative",
        borderRadius: 1,
        overflow: "hidden",
        cursor: clickable ? "pointer" : "default",
        lineHeight: 0,
      }}
    >
      {children}
      <BubbleTime timestamp={timestamp} overlay />
    </Box>
  );

  let body: ReactNode;
  /** Mídia desenha a própria hora sobreposta; o resto usa a hora do rodapé. */
  let ownTime = false;
  /** Bolha com padding reduzido, para a mídia encostar nas bordas. */
  let compact = false;

  switch (message.type) {
    // ------------------------------------------------------------- texto
    case "text":
      body = textBubble(message.text);
      break;

    // -------------------------------- imagem do cliente (só a descrição da IA)
    case "image-description":
      body = (
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Iconify icon="solar:gallery-bold" width={14} />
            <Typography variant="caption" fontWeight={700}>
              Imagem recebida
            </Typography>
          </Stack>

          <Typography variant="body2" sx={innerCardSx}>
            {message.description}
          </Typography>

          {message.caption ? (
            textBubble(message.caption)
          ) : (
            <Box sx={{ height: 4 }}>
              <TimeSpacer />
            </Box>
          )}
        </Stack>
      );
      break;

    // ----------------------------------------------------- mensagem com botões
    case "buttons":
      body = (
        <Stack spacing={0.75}>
          {message.title && (
            <Typography variant="subtitle2" fontWeight={700}>
              {message.title}
            </Typography>
          )}

          {message.description && (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {formatInline(message.description)}
            </Typography>
          )}

          {message.footerText && (
            <Typography variant="caption" color="text.secondary">
              {message.footerText}
            </Typography>
          )}

          {message.sections.map((section, sectionIndex) => (
            <Stack key={`${section.title}-${sectionIndex}`} spacing={0.5}>
              {section.title && (
                <Typography variant="caption" fontWeight={700}>
                  {section.title}
                </Typography>
              )}

              {section.rows.map((row, rowIndex) => (
                <Stack
                  key={`${row.title}-${rowIndex}`}
                  sx={{
                    px: 1,
                    py: 0.75,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="caption" fontWeight={600}>
                    {row.title}
                  </Typography>
                  {row.description && (
                    <Typography variant="caption" color="text.secondary">
                      {row.description}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          ))}

          {message.buttonText && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              justifyContent="center"
              sx={{
                pt: 0.75,
                borderTop: "1px solid",
                borderColor: "divider",
                color: "info.main",
              }}
            >
              <Iconify icon="eva:list-outline" width={16} />
              <Typography variant="caption" fontWeight={700}>
                {message.buttonText}
              </Typography>
            </Stack>
          )}

          <TimeSpacer />
        </Stack>
      );
      break;

    // ------------------------------------------------------------- catálogo
    case "catalog":
      body = (
        <Stack spacing={0.75}>
          {message.intro && (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {formatInline(message.intro)}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 1,
              overflowX: "auto",
              pb: 0.5,
              scrollSnapType: "x mandatory",
              "&::-webkit-scrollbar": { height: 6 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "grey.400",
                borderRadius: 3,
              },
            }}
          >
            {message.cards.map((card, cardIndex) => (
              <Stack
                key={`${card.image}-${cardIndex}`}
                spacing={0.75}
                sx={{
                  scrollSnapAlign: "start",
                  flexShrink: 0,
                  width: message.cards.length > 1 ? 200 : "100%",
                  minWidth: message.cards.length > 1 ? 200 : 220,
                  p: 0.75,
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {card.image && (
                  <Box
                    component="img"
                    src={card.image}
                    alt={card.text || "Item do catálogo"}
                    onClick={() => onOpenMedia(card.image as string)}
                    sx={{
                      width: "100%",
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 0.75,
                      bgcolor: "grey.100",
                    }}
                  />
                )}

                {card.text && (
                  <Typography variant="caption" fontWeight={600}>
                    {card.text}
                  </Typography>
                )}

                {card.actions.map((action, actionIndex) => {
                  const isLink = action.actionType === "URL" && action.url;

                  return (
                    <Box
                      key={`${action.buttonText}-${actionIndex}`}
                      {...(isLink
                        ? {
                            component: "a",
                            href: action.url as string,
                            target: "_blank",
                            rel: "noopener noreferrer",
                          }
                        : {})}
                      sx={{
                        display: "block",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        textAlign: "center",
                        textDecoration: "none",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "info.main",
                        bgcolor: "action.hover",
                      }}
                    >
                      {action.buttonText}
                    </Box>
                  );
                })}
              </Stack>
            ))}
          </Box>

          <TimeSpacer />
        </Stack>
      );
      break;

    // ------------------------------------------------------------------ pix
    case "pix":
      body = (
        <Stack spacing={0.75} sx={{ minWidth: 240 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Iconify icon="ic:round-pix" width={20} />
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Chave Pix
              </Typography>
              {message.amount !== null && (
                <Typography variant="caption" color="text.secondary">
                  {fCurrency(message.amount)}
                </Typography>
              )}
            </Box>
          </Stack>

          {message.merchantName && (
            <Typography variant="caption" color="text.secondary">
              {message.merchantName}
            </Typography>
          )}

          <Typography variant="body2" fontWeight={600} sx={innerCardSx}>
            {message.pixKey || "Chave Pix indisponível"}
          </Typography>

          <Tooltip title="Copiar chave Pix">
            <Box component="span">
              <Button
                fullWidth
                size="small"
                color="success"
                variant="contained"
                disabled={!message.pixKey}
                startIcon={<Iconify icon="solar:copy-linear" />}
                onClick={() => handleCopy(message.pixKey, "Chave Pix")}
              >
                Copiar chave
              </Button>
            </Box>
          </Tooltip>

          <TimeSpacer />
        </Stack>
      );
      break;

    // ------------------------------------------------------------------ otp
    case "otp":
      body = (
        <Stack spacing={0.75} sx={{ minWidth: 240 }}>
          {message.text && (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {formatInline(message.text)}
            </Typography>
          )}

          <Typography
            variant="h6"
            sx={{ ...innerCardSx, textAlign: "center", letterSpacing: 2 }}
          >
            {message.code}
          </Typography>

          <Button
            fullWidth
            size="small"
            variant="contained"
            startIcon={<Iconify icon="solar:copy-linear" />}
            onClick={() => handleCopy(message.code, "Código")}
          >
            {message.buttonText}
          </Button>

          <TimeSpacer />
        </Stack>
      );
      break;

    // --------------------------------------------------------- imagem e gif
    case "image":
    case "sticker":
    case "gif":
      if (message.media.isExpired) {
        body = <ExpiredMedia expiresAt={message.media.expiresAt} />;
        break;
      }

      ownTime = true;
      compact = true;
      body =
        message.type === "sticker" ? (
          mediaFrame(
            message.media.url,
            <Box
              component="img"
              src={message.media.url}
              alt="Sticker"
              sx={{ width: 140, display: "block" }}
            />,
            false
          )
        ) : (
          mediaFrame(
            message.media.url,
            <Box
              component="img"
              src={message.media.url}
              alt={message.type === "gif" ? "GIF" : "Imagem"}
              sx={{
                width: 280,
                maxWidth: "100%",
                display: "block",
                bgcolor: "grey.100",
              }}
            />
          )
        );
      break;

    // ---------------------------------------------------------------- vídeo
    case "video":
      if (message.media.isExpired) {
        body = <ExpiredMedia expiresAt={message.media.expiresAt} />;
        break;
      }

      ownTime = true;
      compact = true;
      body = mediaFrame(
        message.media.url,
        <Box
          component="video"
          src={message.media.url}
          controls
          sx={{ width: 280, maxWidth: "100%", display: "block" }}
        />,
        false
      );
      break;

    // ---------------------------------------------------------------- áudio
    case "audio":
      body = message.media.isExpired ? (
        <ExpiredMedia expiresAt={message.media.expiresAt} />
      ) : (
        <Stack spacing={0.5}>
          <Box
            component="audio"
            controls
            src={message.media.url}
            sx={{ width: 250, maxWidth: "100%", display: "block" }}
          />
          <TimeSpacer />
        </Stack>
      );
      break;

    // ------------------------------------------------------------ documento
    case "document": {
      if (message.media.isExpired) {
        body = <ExpiredMedia expiresAt={message.media.expiresAt} />;
        break;
      }

      const extension = getFileExtension(message.media.url);

      body = (
        <Stack spacing={0.75} sx={{ minWidth: 220 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={innerCardSx}>
            <Iconify icon={getDocumentIcon(extension)} width={28} />
            <Typography variant="subtitle2" fontWeight={700}>
              Documento {extension ? extension.toUpperCase() : ""}
            </Typography>
          </Stack>

          <Button
            fullWidth
            component="a"
            href={message.media.url}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="contained"
            startIcon={<Iconify icon="eva:download-outline" width={16} />}
          >
            Baixar
          </Button>

          <TimeSpacer />
        </Stack>
      );
      break;
    }

    // ----------------------------------------------------------- localização
    case "location": {
      const query =
        message.latitude && message.longitude
          ? `${message.latitude},${message.longitude}`
          : message.address || message.name || "";

      body = (
        <Stack spacing={0.75} sx={{ minWidth: 220 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Iconify icon="solar:map-point-bold" width={18} />
            <Typography variant="subtitle2" fontWeight={700}>
              {message.name || "Localização"}
            </Typography>
          </Stack>

          {message.address && (
            <Typography variant="body2" color="text.secondary">
              {message.address}
            </Typography>
          )}

          {query && (
            <Button
              fullWidth
              component="a"
              href={`https://www.google.com/maps?q=${encodeURIComponent(query)}`}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="contained"
              startIcon={<Iconify icon="eva:external-link-fill" width={16} />}
            >
              Abrir no mapa
            </Button>
          )}

          <TimeSpacer />
        </Stack>
      );
      break;
    }

    // -------------------------------------------------------------- fallback
    case "unknown":
      body = (
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Iconify icon="eva:alert-triangle-outline" width={16} />
            <Typography variant="subtitle2" fontWeight={700}>
              Mensagem não suportada
            </Typography>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {message.reason}
          </Typography>

          {message.preview && (
            <Typography
              variant="caption"
              sx={{ ...innerCardSx, wordBreak: "break-all" }}
            >
              {message.preview}
            </Typography>
          )}

          <TimeSpacer />
        </Stack>
      );
      break;

    default: {
      // Se o TS reclamar aqui, um tipo novo entrou no union sem tratamento.
      const exhaustive: never = message;
      body = null;
      // eslint-disable-next-line no-console
      console.warn("Tipo de mensagem sem renderização", exhaustive);
    }
  }

  return (
    <Stack
      direction="row"
      justifyContent={isClient ? "flex-start" : "flex-end"}
      sx={{
        mt: isFirstOfBlock ? 1.25 : 0.25,
        // Bolhas sem rabicho alinham a borda com a que tem.
        ...(isClient ? { pl: isFirstOfBlock ? 0 : "7px" } : { pr: isFirstOfBlock ? 0 : "7px" }),
      }}
    >
      <Box sx={bubbleSx(theme, { isClient, isFirstOfBlock, compact })}>
        {isFirstOfBlock && (
          <BubbleTail isClient={isClient} color={bubbleColor(theme, isClient)} />
        )}

        {isFirstOfBlock && <SenderTag sender={message.sender} theme={theme} />}

        {body}

        {!ownTime && <BubbleTime timestamp={timestamp} />}
      </Box>
    </Stack>
  );
}

export default memo(ChatMessageItem);
